import { Search } from '@mui/icons-material'
import NotificationList from './NotificationList'
import { Popover, Modal, IconButton, InputAdornment, Toolbar, Typography } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import SonyHDLogo from '../../../Assets/Images/sonyHDlogo.png'
import CONSTANTS, { PAGES_ROUTES } from '../../../Constants'
import { getLocalStorage } from '../../../Helpers/Storage'
import { logout } from '../../../Helpers/Util'
import { AppBar } from '../MUI-Drawer/MUI-Drawer-Style'
import { BoltLogo } from '../MUI-Drawer/MUI-Drawer.svgIcons'
import Badge from '@mui/material/Badge'
import SettingIcon from '../../Icons/SettingIcon'
import NotificationIcon from '../../../Assets/Images/svgs/notification-icon'
import AccountCircle from '../../../Assets/Images/svgs/account-circle'
import ProfileMenu from './ProfileMenu'
import mainApiService from '../../../Services'
import { NOTIFICATIONS } from '../../../Constants/ApiDefinations'
import styles from './Header.module.scss'
import MUIFieldWithoutControl from '../MUI-Field-Without-Control'
import { CustomTooltip } from '../CustomToolTip/CustomToolTip'
import { logger } from '../../../Helpers'

const Header: React.FC<{ setOpen?: any; drawerState?: boolean; sidebar?: boolean }> = ({
  setOpen = () => {
    return
  },
  drawerState = false,
  sidebar = true,
}: {
  setOpen?: any
  drawerState?: boolean
  sidebar?: boolean
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = Boolean(anchorEl)
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [notificationCount, setNotificationCount] = useState(0)
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null)

  const navigate = useNavigate()
  const location = useLocation()
  const maintenanceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const userData = getLocalStorage(CONSTANTS.LOCAL_KEYS.USER_DATA)
    const { userName = '' } = userData
    const { userEmail = '' } = userData
    if (userName) {
      setUserName(userName)
    }
    if (userEmail) {
      setUserEmail(userEmail)
    }
  }, [drawerState])

  useEffect(() => {
    if (location.pathname === PAGES_ROUTES.UnderMaintenance) {
      return
    }
    handleNotificationCount()
    const interval = setInterval(() => {
      if (location.pathname !== PAGES_ROUTES.UnderMaintenance) {
        handleNotificationCount()
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [location.pathname])

  useEffect(() => {
    return () => {
      if (maintenanceTimeoutRef.current) {
        clearTimeout(maintenanceTimeoutRef.current)
        maintenanceTimeoutRef.current = null
      }
    }
  }, [])

  const handleNotificationCount = async () => {
    if (location.pathname === PAGES_ROUTES.UnderMaintenance) {
      return
    }
    try {
      const notifications = NOTIFICATIONS.NOTIFICATION_COUNT()
      const response: any = await mainApiService(notifications)
      setNotificationCount(response?.data?.unseenCount)
    } catch (error) {
      logger.error(error)
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationClick = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget)
    try {
      const allAsSeen = NOTIFICATIONS.NOTIFICATION_MARK_AS_SEEN()
      const response: any = await mainApiService(allAsSeen)
      if (response?.responseCode === 200) {
        setNotificationCount(0)
      }
    } catch (error) {
      logger.error(error)
    }
  }

  const handleEnterKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      if (searchInput.trim() && searchInput.length > 2)
        navigate(`/${PAGES_ROUTES.globalSearch}?search=${searchInput}`)
    }
  }
  const getNotificationCount = () => {
    if (!notificationCount) return null
    if (notificationCount > 9) return '9+'
    return notificationCount
  }

  return (
    <>
      <AppBar position='fixed' open={drawerState} style={{ backgroundColor: '#191D23' }}>
        <Toolbar className='justify-content-between'>
          <div className='d-flex header-logo'>
            {sidebar && (
              <IconButton
                color='inherit'
                aria-label='open drawer'
                onClick={() => {
                  setOpen(!drawerState)
                }}
                edge='start'
                sx={{
                  marginRight: 2,
                }}
                data-testid='open_drawer_btn'
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link to='/home'>
              <img
                src={SonyHDLogo}
                className={styles['header-logo-img']}
                alt='sony-logo'
                data-testid='sonyLogo_navigate_btn'
              />
            </Link>
            <Link to='/home'>
              <Typography
                component='div'
                sx={{ flexGrow: 1 }}
                className={styles['ml-2'] + ' cursor-pointer'}
              >
                <BoltLogo />
              </Typography>
            </Link>
          </div>
          <div className='d-flex align-items-center'>
            <div className='advanced-search mr-2'>
              <MUIFieldWithoutControl
                className='global-search-input'
                id='outlined-search'
                showClearIcon
                onChange={(e: any) => {
                  setSearchInput(e)
                }}
                value={searchInput}
                onKeyDown={handleEnterKeyPress}
                placeholder={'Search by title and ID'}
                InputProps={{
                  id: 'searchId',
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search style={{ color: '#B1B4B6' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <CustomTooltip
                title='Advance Filter'
                placement='bottom-start'
                arrow
                classes={{
                  tooltip: styles['header-tooltip'],
                  arrow: styles['header-tooltip-arrow'],
                }}
                data-testid={'open_drawer_test_id'}
              >
                <Link to={`/${PAGES_ROUTES.AdvancedSearch}`}>
                  <div data-testid='advance_btn' className='search-advanced-btn'>
                    <SettingIcon />
                  </div>
                </Link>
              </CustomTooltip>
            </div>
            <div className='d-flex align-items-center'>
              <CustomTooltip
                title='Notifications'
                placement='bottom-start'
                arrow
                classes={{
                  tooltip: styles['header-tooltip'],
                  arrow: styles['header-tooltip-arrow'],
                }}
              >
                <IconButton
                  size='large'
                  aria-label='notification of current user'
                  aria-controls='menu-appbar'
                  aria-haspopup='true'
                  color='inherit'
                  onClick={handleNotificationClick}
                >
                  <Badge
                    badgeContent={getNotificationCount()}
                    color='error'
                    overlap='circular'
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    sx={{
                      '& .MuiBadge-badge': {
                        left: '10px',
                        top: '-10px',
                      },
                    }}
                  ></Badge>
                  <NotificationIcon />
                </IconButton>
              </CustomTooltip>
              <CustomTooltip
                title='View profile & settings'
                placement='bottom-start'
                arrow
                classes={{
                  tooltip: styles['header-tooltip'],
                  arrow: styles['header-tooltip-arrow'],
                }}
              >
                <IconButton
                  size='large'
                  aria-label='account of current user'
                  aria-controls='menu-appbar'
                  aria-haspopup='true'
                  onClick={handleClick}
                  color='inherit'
                >
                  <AccountCircle />
                </IconButton>
              </CustomTooltip>
              <Modal
                open={openMenu}
                onClose={handleClose}
                aria-labelledby='profile-menu-modal'
                aria-describedby='profile-menu-modal-description'
                className={styles['modal-profile-menu-backdrop']}
                disableEnforceFocus
                disableRestoreFocus
              >
                <div className={styles['modal-profile-menu-backdrop']}>
                  <div className={styles['profile-menu-inner']}>
                    <ProfileMenu
                      userName={userName}
                      userEmail={userEmail}
                      onLogout={logout}
                      onClose={handleClose}
                      onListingPref={() => {
                        handleClose()
                        navigate(PAGES_ROUTES.ListingScreenPreference)
                      }}
                      onNotificationPref={() => {
                        handleClose()
                        navigate(PAGES_ROUTES.NotificationPreference)
                      }}
                      onMaintenanceToggle={(checked) => {
                        if (maintenanceTimeoutRef.current) {
                          clearTimeout(maintenanceTimeoutRef.current)
                        }
                        maintenanceTimeoutRef.current = setTimeout(() => {
                          navigate(checked ? PAGES_ROUTES.UnderMaintenance : PAGES_ROUTES.HomePage)
                          maintenanceTimeoutRef.current = null
                        }, 500)
                      }}
                    />
                  </div>
                </div>
              </Modal>
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <Popover
        open={Boolean(notificationAnchorEl)}
        anchorEl={notificationAnchorEl}
        onClose={() => setNotificationAnchorEl(null)}
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <NotificationList onClose={() => setNotificationAnchorEl(null)} />
      </Popover>
    </>
  )
}

export default Header
